// Single source of truth for every /blog/* permanent (301) redirect.
//
// Two consumers share this list so they can never drift apart:
//   1. next.config.mjs spreads `BLOG_REDIRECTS` into redirects() — the actual
//      301s served at the route layer.
//   2. app/sitemap.ts and app/sitemap-image.xml/route.ts filter
//      `REDIRECTED_BLOG_SLUGS` out of the sitemap, so a redirected URL can
//      never be advertised as canonical.
//
// Why this file exists: the route-level redirect and the post's CMS lifecycle
// (status: published | archived in Directus) are two independent systems.
// Nothing forced a redirected slug to also be de-published, so the 2026-05-17
// CNI recovery sprint added 301s but left 11 source records `published` — they
// kept appearing in both sitemaps and tripped GSC's "wrong pages found in
// sitemap" error (discovered 2026-06-04). With this guard, the sitemap
// auto-excludes any redirected slug even if the Directus archive step is
// missed. Archiving the record is still correct (it's the canonical state),
// but it's no longer load-bearing for sitemap correctness.
//
// When you consolidate or rename a blog post: add the redirect HERE (not
// inline in next.config), then archive the old record in Directus.

/** @typedef {{ source: string, destination: string, permanent: boolean }} Redirect */

/** @type {Redirect[]} */
export const BLOG_REDIRECTS = [
  // --- Comparison blog posts → canonical /compare/ pages (2026-05-15) ---
  // The /compare/ versions are richer, structured, and emit BreadcrumbList +
  // FAQPage schema; the blog posts only existed as ad-hoc narrative.
  { source: '/blog/fescue-vs-bermuda-which-is-better-for-your-lawn', destination: '/compare/fescue-vs-bermuda', permanent: true },
  { source: '/blog/st-augustine-vs-bermuda-grass-you-plant', destination: '/compare/bermuda-vs-st-augustine', permanent: true },
  { source: '/blog/fine-fescue-vs-tall-fescue-differences-pros-cons', destination: '/compare/fine-vs-tall-fescue', permanent: true },

  // --- Content consolidation: cannibalized posts → canonical versions ---
  // Grass identification (2 → 1).
  { source: '/blog/how-to-identify-your-grass-type', destination: '/blog/how-to-identify-your-grass-type-by-look-feel', permanent: true },
  // Legacy slug variant that returned 500 in production.
  { source: '/blog/how-to-identify-grass-type', destination: '/blog/how-to-identify-your-grass-type-by-look-feel', permanent: true },
  { source: '/blog/when-to-fertilize-bermuda-grass', destination: '/blog/when-to-fertilize-your-lawn-simple-timing-rules-for-every-grass-type', permanent: true },
  { source: '/blog/bermuda-grass-fertilizer-schedule', destination: '/blog/best-fertilizer-for-bermuda-grass-top-picks-reviews', permanent: true },
  // Slug rename 2026-05: removed stale "-in-2025-" segment. Keep indefinitely —
  // external backlinks still point at the old URL.
  { source: '/blog/best-fertilizer-for-bermuda-grass-in-2025-top-picks-reviews', destination: '/blog/best-fertilizer-for-bermuda-grass-top-picks-reviews', permanent: true },
  // Lawn care calendar (4 → 1).
  { source: '/blog/the-ultimate-seasonal-lawn-care-calendar', destination: '/blog/monthly-lawn-care-calendar', permanent: true },
  { source: '/blog/the-ultimate-diy-lawn-care-calendar-what-to-do-month-by-month', destination: '/blog/monthly-lawn-care-calendar', permanent: true },
  { source: '/blog/how-to-create-a-lawn-maintenance-schedule', destination: '/blog/monthly-lawn-care-calendar', permanent: true },
  // Weed & feed (2 → 1).
  { source: '/blog/weed-and-feed-application-guide-best-products', destination: '/blog/scotts-weed-and-feed-application-guide-best-products', permanent: true },
  // Lawn care tools (2 → 1) — the verbose slug duplicated the cleaner one.
  { source: '/blog/the-essential-lawn-care-toolkit-tools-every-homeowner-should-own-and-how-to-use-them', destination: '/blog/essential-lawn-care-tools-every-homeowner-needs', permanent: true },
  // St. Augustine species guide (2 → 1, 2026-08-05): the blog guide survived the
  // 2026-05/06 pillar consolidation and split the species intent with the
  // /guides/ pillar (blog @9.4 vs pillar @13.4 in GSC). Unique sections grafted
  // into data/guides/st-augustinegrass.ts before archiving the blog record.
  { source: '/blog/st-augustinegrass-guide', destination: '/guides/st-augustinegrass', permanent: true },

  // --- Truncated-slug recovery (Directus had cut the slug mid-word). Slugs
  // corrected at the source 2026-05-02; these 301s catch incoming traffic on
  // the old broken URLs. ---
  { source: '/blog/brown-patch-disease-fescue-diagnosis-organic-treatment-optio', destination: '/blog/brown-patch-disease-fescue-diagnosis-organic-treatment-options', permanent: true },
  { source: '/blog/complete-spring-lawn-aeration-guide-timing-equipment-techniq', destination: '/blog/complete-spring-lawn-aeration-guide-timing-equipment-technique', permanent: true },
  { source: '/blog/preventing-spring-dead-spot-winter-essential-steps-healthy-l', destination: '/blog/preventing-spring-dead-spot-winter-essential-steps-healthy-lawn', permanent: true },
  { source: '/blog/scotts-turf-builder-triple-action-instructions-complete-appl', destination: '/blog/scotts-turf-builder-triple-action-instructions-complete-application-guide', permanent: true },
  // The un-suffixed slug (no "-guide") was indexed by Google and still earns ~169
  // clicks/90d but 404s — the "-appl" redirect above only catches the older truncated
  // slug, not this full-word one. Added 2026-06-19 (affiliate audit). ---
  { source: '/blog/scotts-turf-builder-triple-action-instructions-complete-application', destination: '/blog/scotts-turf-builder-triple-action-instructions-complete-application-guide', permanent: true },
  { source: '/blog/pennington-smart-seed-planting-guide-step-by-step-instructio', destination: '/blog/pennington-smart-seed-planting-guide-step-by-step-instructions', permanent: true },
  // Second truncation batch (2026-09-14 affiliate audit). The 2026-05-02 sweep
  // fixed the slugs in Directus but only covered part of the family; these four
  // stayed indexed and ranking (162 clicks / 34,749 impressions per 90 days,
  // top-dressing alone at position 6.5) while serving the not-found shell.
  { source: '/blog/most-common-top-dressing-compost-peat-moss-topsoil-sand-which-is-be', destination: '/blog/most-common-top-dressing-compost-peat-moss-topsoil-sand-which-is-best-for-you', permanent: true },
  { source: '/blog/when-to-fertilize-your-lawn-simple-timing-rules-for-every-grass-typ', destination: '/blog/when-to-fertilize-your-lawn-simple-timing-rules-for-every-grass-type', permanent: true },
  { source: '/blog/managing-salt-damage-on-lawns-winter-recovery-and-prevention-strate', destination: '/blog/managing-salt-damage-on-lawns-winter-recovery-and-prevention-strategies', permanent: true },
  { source: '/blog/new-homeowner-lawn-care-checklist-everything-to-do-in-your-first-ye', destination: '/blog/new-homeowner-lawn-care-checklist-everything-to-do-in-your-first-year', permanent: true },
  // Same audit, zero-traffic tail of the same not-found set: mangled slugs that
  // still hold 1-2 impressions each. Each destination was curl-verified live.
  // `/blog/best-fertilizer-for-texas-grass-in-texas` is deliberately left to
  // 404 — Directus has bermuda, St. Augustine and zoysia Texas fertilizer posts
  // and no rule picks a winner.
  { source: '/blog/best-weed-control-bermuda-grass-safe-products-wont-lawn-wont-kill', destination: '/blog/best-weed-control-bermuda-grass-safe-products-wont-kill', permanent: true },
  { source: '/blog/ortho-groundclear-vs-ortho-groundclear-vs-ortho-groundclear-vs-roun', destination: '/blog/ortho-groundclear-vs-roundup-which-to-use', permanent: true },
  { source: '/blog/scotts-weed-feed-application-guide-best-products', destination: '/blog/scotts-weed-and-feed-application-guide-best-products', permanent: true },
  { source: '/blog/specracide-weed-stop-instructions-use-best-results', destination: '/blog/spectracide-weed-stop-instructions-use-best-results', permanent: true },
  { source: '/blog/summer-dormancy-cool-season-grass', destination: '/blog/summer-lawn-dormancy-cool-season-grass', permanent: true },

  // --- "-1" suffix cleanup: slug-generator artifacts with no matching
  // unsuffixed sibling (2026-05-02). ---
  { source: '/blog/creative-lawn-edging-ideas-1', destination: '/blog/creative-lawn-edging-ideas', permanent: true },
  { source: '/blog/scotts-southern-turf-builder-instructions-1', destination: '/blog/scotts-southern-turf-builder-instructions', permanent: true },
  { source: '/blog/scotts-turf-builder-halts-crabgrass-preventer-instructions-1', destination: '/blog/scotts-turf-builder-halts-crabgrass-preventer-instructions', permanent: true },

  // Phantom slug variant serving a 200-OK fallback with no canonical tag.
  { source: '/blog/causes-of-dollar-spots', destination: '/blog/causes-dollar-spots', permanent: true },

  // === CNI recovery batch — 2026-05-17 audit ===
  // Posts stuck in GSC "Crawled - currently not indexed" due to self-
  // cannibalization + duplication with /guides/ pillars. Redirects consolidate
  // signal into the strongest "keeper" per cluster. See
  // obsidian/decisions/2026-05-17-cni-recovery-sprint.md for keeper rationale.
  // Group A — blog → /guides/ pillar 301s.
  { source: '/blog/the-complete-lawn-watering-guide', destination: '/guides/watering', permanent: true },
  { source: '/blog/kentucky-bluegrass-seed-selection-planting-guide', destination: '/guides/kentucky-bluegrass', permanent: true },
  // Group C — self-cannibalization across clusters.
  { source: '/blog/avoiding-overwatering-mistakes', destination: '/blog/signs-of-overwatering-grass', permanent: true },
  { source: '/blog/diy-lawn-pest-control-safe-options-that-actually-work', destination: '/blog/natural-lawn-pest-control-pet-safe-and-kid-safe-approaches', permanent: true },
  { source: '/blog/chemicalfree-lawn-care-tips', destination: '/blog/natural-lawn-pest-control-pet-safe-and-kid-safe-approaches', permanent: true },
  // Winter hub re-pointed 2026-07-19. The 2026-05-17 sprint made
  // winter-lawn-treatment and winter-lawn-protection-care the keepers, but a
  // 12-month GSC review found both flat (0 clicks; 26 and 18 lifetime
  // impressions) while protect-grass-in-winter earned 1,441 impressions on the
  // same intent. These three now point straight at the new hub instead of
  // chaining through the demoted keepers — validateRedirects rejects chains and
  // Next.js does not follow them in a single pass.
  { source: '/blog/winter-lawncare-for-spring', destination: '/blog/protect-grass-in-winter', permanent: true },
  { source: '/blog/lawn-preparation-january', destination: '/blog/protect-grass-in-winter', permanent: true },
  { source: '/blog/maintaining-cold-weather-lawns-a-winter-lawn-care-guide', destination: '/blog/protect-grass-in-winter', permanent: true },
  // Group E — archive bucket (<20 lifetime impressions each).
  { source: '/blog/how-to-budget-for-lawn-care', destination: '/blog/lawn-care-cost-breakdown-2026', permanent: true },
  { source: '/blog/3-biggest-lawn-care-barriers-and-how-to-beat-them', destination: '/blog/lawn-care-101-the-complete-beginners-guide-to-a-healthy-green-yard', permanent: true },
  { source: '/blog/how-to-test-your-lawns-soil', destination: '/blog/how-to-improve-soil-ph-for-grass', permanent: true },
  { source: '/blog/how-long-does-a-lawn-take-to-grow-expert-tips-guide', destination: '/blog/growing-grass-from-seed-how-long-complete-care-guide', permanent: true },

  // --- Duplicate consolidation (2026-06-05): "Is Scotts Weed and Feed a
  // Pre-Emergent" had two published copies. Keeper is id 318
  // (scotts-weed-feed-pre-emergent): older, ~4.4k impr, and the one the Scotts
  // pillar links to. id 339 (is-scotts-weed-and-feed-a-pre-emergent) is the
  // redundant copy — its content is already covered by the keeper and its
  // "scotts turf builder" relevance transfers via this 301. See
  // obsidian/changelog/2026-06.md. ---
  { source: '/blog/is-scotts-weed-and-feed-a-pre-emergent', destination: '/blog/scotts-weed-feed-pre-emergent', permanent: true },

  // === Pillar consolidation — 2026-06-14 (extends the 2026-05-17 CNI sprint) ===
  // Grass-type blog "guide" posts that duplicate their /guides/[slug] pillar,
  // graduated to a safe merge after URL-inspecting the pillars on 2026-06-14 and
  // confirming they are now indexed (the sprint's Group B "hold until indexed").
  // Bermuda first: its blog was returning 5xx (JSDOM dynamic-SSR bug) since
  // 2026-04-30 while /guides/bermudagrass is indexed with the fuller care program,
  // so this is a zero-risk merge. Pair rule: source record archived in Directus.
  // Follow-up: fold each blog's unique cultivar/varieties table into its pillar
  // (Bermuda: common/hybrid/seeded — TifTuf, Tifway 419, Yukon, Princess 77;
  //  Zoysia: Emerald/Meyer/Zeon/El Toro/Zenith comparison table).
  { source: '/blog/bermudagrass-guide', destination: '/guides/bermudagrass', permanent: true },
  // Zoysia: pillar (pos 16.8, indexed) is the stronger keeper vs the blog
  // (pos 28.8, ~69 impr — within the CNI Group-A straight-redirect threshold).
  { source: '/blog/zoysia-grass-complete-guide', destination: '/guides/zoysiagrass', permanent: true },
  // Centipede: pillar is indexed and stronger blended (pos 14.8) than the blog;
  // on the bare "centipede grass" term the blog (35.1) edged the pillar (63.7),
  // and the blog was also cannibalizing other grasses' terms (ranking ~pos 2 for
  // zoysia/buffalo/KBG). Consolidating ends the split and the cross-term noise.
  // Watch the pillar's "centipede grass" position post-deploy. Follow-up: fold
  // the blog's cultivar list (Common/TifBlair/Oaklawn/Santee) into the pillar.
  { source: '/blog/centipede-grass-guide-lazy-mans-grass-explained', destination: '/guides/centipede', permanent: true },

  // --- Duplicate consolidation (2026-06-16): "Best Fertilizer for St. Augustine
  // Grass (2026 Guide)" had two published copies with identical titles. Keeper is
  // id 344 (best-fertilizer-FOR-st-augustine-grass-2026-guide): 4.3k words, 9
  // tagged affiliate products, stronger in GSC (19k impr, pos 7.7 vs the
  // retiree's 4.5k impr, pos 8.9). The retiree's lone ASIN (B07SYLCKRH) is
  // already 344's lead product, so no affiliate revenue is lost; its four unique
  // content blocks (FAQ, pre-fert checklist, worked example, specialized picks)
  // were grafted into 344 first via scripts/consolidate-st-augustine-fertilizer.ts.
  // Archive id 292 in Directus after this 301 deploys. See obsidian/changelog/2026-06.md.
  { source: '/blog/best-fertilizer-st-augustine-grass-2026-guide', destination: '/blog/best-fertilizer-for-st-augustine-grass-2026-guide', permanent: true },

  // --- Duplicate consolidation (2026-06-17): two published "Methylated Seed Oil
  // (MSO)" posts split the head term. Keeper is id 178
  // (methylated-seed-oil-the-complete-guide): the comprehensive technical guide
  // (mixing rates, MSO vs surfactant, safety) that already ranks for the full
  // MSO long-tail — "methylated seed oil" + "per gallon" + "surfactant" +
  // "nonionic surfactant" (~14 clicks / 3.1k impr). The retiree id 332
  // (methylated-seed-oil-mso-you) is thinner, was miscategorized under
  // "Seeding/Overseeding" (it's an herbicide surfactant), and ranked only for
  // the bare head term (~2 clicks / 568 impr) — an intent the keeper already
  // covers, so this straight redirect loses no unique query. No content graft
  // needed. Archive id 332 in Directus after this 301 deploys. See obsidian/changelog/2026-06.md.
  { source: '/blog/methylated-seed-oil-mso-you', destination: '/blog/methylated-seed-oil-the-complete-guide', permanent: true },

  // === Cannibalization consolidation — 2026-06-19 (weed-and-feed "how long to work" cluster) ===
  // Three posts split the "how long does weed and feed take to work" intent. Keeper is
  // id 316 (long-scotts-weed-feed-take-work): owns the entire timing variant set at
  // pos 2-4 (~1.4k clicks / 60d). The two newer posts only diluted it:
  //   - id 341 (...-timeline) ranked pos 9.2 for the SAME head query 316 wins at 4.2.
  //     Its unique granular-vs-liquid timelines, 5-brand comparison table, "two clocks"
  //     model, and weed-type/weed-age speed factors were grafted into 316 first via
  //     scripts/consolidate-weed-feed-into-scotts-timeline.ts.
  //   - id 331 (...-3-7-days) had 0 GSC rows in 60d (dead duplicate); its unique
  //     "special situations" block (newly-seeded / organic alternatives) grafted too.
  // Archive ids 341 + 331 in Directus after this 301 deploys. See
  // obsidian/decisions/2026-06-19-seo-content-cannibalization-routing.md.
  { source: '/blog/how-long-does-weed-and-feed-take-to-work-timeline', destination: '/blog/long-scotts-weed-feed-take-work', permanent: true },
  { source: '/blog/long-weed-feed-take-work-3-7-days-day-by-day', destination: '/blog/long-scotts-weed-feed-take-work', permanent: true },

  // === Straggler consolidation — 2026-06-19 (rescue pass on stalled 2-month cohort) ===
  // id 147 (types-of-grass-for-lawns...) sat at pos 27 blended / pos 40-99 on its "types
  // of grass" queries — Google already treats it as redundant with the stronger grass-ID
  // keeper id 342 (what-kind-of-grass-do-i-have-visual-identification-guide, pos 6-7, the
  // comprehensive blade/tip/vernation/photo chart). 342 already covers 147's per-species
  // ID descriptors in chart form, so this is a straight redirect (no graft). Archive id
  // 147 after this 301 deploys.
  { source: '/blog/types-of-grass-for-lawns-how-to-identify-what-you-have', destination: '/blog/what-kind-of-grass-do-i-have-visual-identification-guide', permanent: true },
  // id 325 (best-practices-summer-lawn-fertilization-bloom) was a confused lawn+flower
  // post stuck at pos 13-46 (0 clicks). Keeper id 321 (best-summer-lawn-fertilizers-keep-
  // grass-green-healthy) owns the summer-fertilizer intent; 325's unique grass NPK table,
  // slow-vs-quick-release explainer, summer timing windows, and drought/shade rate
  // adjustments were grafted into 321 first via
  // scripts/consolidate-summer-fertilizer-into-keeper.ts (its bloom/groundcover half
  // dropped as off-topic). Archive id 325 after this 301 deploys.
  { source: '/blog/best-practices-summer-lawn-fertilization-bloom', destination: '/blog/best-summer-lawn-fertilizers-keep-grass-green-healthy', permanent: true },

  // === Worst-posts rescue — 2026-07-10 (bottom of the 60d GSC page report) ===
  // Five sources whose intent a stronger sibling already owns. Query-level GSC
  // showed each ranking 25-90 on its real head terms while the keeper ranks
  // page 1-2 for the same intent. Straight redirects (no grafts): the keepers
  // already cover the content, and 267's "2026 Edition" product list did not
  // survive verification. Archive sources via
  // scripts/rescue-worst-posts.ts --archive --apply AFTER these deploy. See
  // obsidian/decisions/2026-07-10-worst-posts-rescue.md.
  // id 267 (3.9k words) duplicated keeper id 44 (best-grass-for-hightraffic-areas,
  // 21 clicks/6.9k impr vs 2/3.0k) on the same traffic-tolerance intent.
  { source: '/blog/best-grass-seed-high-traffic-areas-2026', destination: '/blog/best-grass-for-hightraffic-areas', permanent: true },
  // id 326: generic summer-IPM strategy fully covered by the pest-ID keeper (id 142).
  { source: '/blog/dealing-summer-lawn-pests-effective-strategies', destination: '/blog/lawn-pest-control-identify-common-bugs-and-get-rid-of-them-fast', permanent: true },
  // id 14: 1.2k-word thin beginner primer; keeper id 129 is the established
  // beginner hub (3-biggest-barriers already 301s there).
  { source: '/blog/the-beginners-guide-to-a-healthy-green-lawn', destination: '/blog/lawn-care-101-the-complete-beginners-guide-to-a-healthy-green-yard', permanent: true },
  // id 52: head terms ranked 24-91; keeper id 131 already has a dedicated
  // "Best Time of Day to Water Your Lawn" H2 section — full redundancy.
  { source: '/blog/best-time-of-day-to-water-your-lawn', destination: '/blog/watering-your-lawn-the-right-way-how-often-and-how-much-to-water', permanent: true },
  // id 49: step-for-step overlap with keeper id 93 (overseeding-best-practices),
  // which already owns fall timing ("Why Fall Is the Best Time" section).
  { source: '/blog/fall-lawn-overseeding-prep-guide', destination: '/blog/overseeding-best-practices', permanent: true },

  // --- Winter cluster consolidation (2026-07-19) ---
  // Five live posts targeted generic "winter lawn care" and split the signal
  // between them. 12-month GSC: protect-grass-in-winter 1,441 impressions vs
  // 301 / 46 / 26 / 18 for the four below, all at 0 clicks. Straight redirects,
  // no grafts — the hub already covers the material and the sources are
  // AI-drafted near-duplicates of it. Records stay in Directus (archived), so
  // nothing is destroyed. Geo-targeted winter pages (California, Florida) are
  // deliberately NOT merged: California is the cluster's best performer at
  // 59 clicks / 17,017 impressions and serves a distinct intent.
  { source: '/blog/winter-lawn-protection-care', destination: '/blog/protect-grass-in-winter', permanent: true },
  { source: '/blog/winter-lawn-treatment-prepare-your-grass-for-cold', destination: '/blog/protect-grass-in-winter', permanent: true },
  { source: '/blog/how-to-landscape-for-winter', destination: '/blog/protect-grass-in-winter', permanent: true },
  { source: '/blog/mastering-lawn-winter-hydration', destination: '/blog/protect-grass-in-winter', permanent: true },

  // Winterizer fertilizer (2 → 1). Textbook cannibalization: both pages rank for
  // the same commercial cluster and both are stuck off page 1. The source held
  // "best lawn winterizer" (pos 12.2), "best lawn fertilizer for winter" (16.5),
  // "best fertilizer for winterizing lawn" (58.8), "…winter grass" (59.4),
  // "…fescue in winter" (70.2) while the keeper sat at 17-19 on the same terms.
  // Keeper wins on every axis: 17 clicks / 14,334 impr / pos 19.5 vs 1 / 746 / 25.0.
  { source: '/blog/best-winterizer-fertilizer-northern-lawn', destination: '/blog/best-winter-lawn-fertilizer-complete-guide', permanent: true },

  // Post-winter damage repair (2 → 1). Direction set by query profile, NOT by
  // impressions — the source had 2.3x the impressions (2,268 vs 1,005) but its
  // footprint is almost entirely irrelevant long-tail noise ("bermuda grass seed
  // 50 lb bag", "best crabgrass removal tool", "5000 square feet") ranking 2-4 on
  // one-impression queries. The keeper owns the actual intent: "how to fix a
  // patchy lawn after winter" at 9.02% CTR, plus the bare-patch/bare-spot cluster.
  { source: '/blog/how-to-fix-winter-lawn-damage-in-spring', destination: '/blog/fix-patchy-lawn-spots-after-winter-easy-solutions', permanent: true },

  // Winter disease (2 → 1). id 234 was ~70% a paraphrase of id 190 — same
  // intro→fast-answer→science→ID→prevention→treatment→"What Other Guides Miss"
  // skeleton, re-run under a different writer persona. Keeper 190 has been
  // rebuilt as the snow mold head-term page (see
  // scripts/rebuild-snow-mold-post.ts); its named fungicide actives and the MSU
  // late-fall nitrogen figure were grafted from 234 before this redirect.
  { source: '/blog/managing-winter-lawn-diseases-your-guide-to-a-healthy-winter-lawn', destination: '/blog/winter-lawn-mold-prevention-treatment', permanent: true },

  // Ghost-slug capture (2026-08-07): these slugs never existed on the site but
  // are indexed and collect impressions (garbled inbound links, likely LLM
  // citations — see the GSC oddities sweep in obsidian/changelog/2026-08.md).
  // Only unambiguous targets get a 301; ambiguous ghosts intentionally 404.
  { source: '/blog/best-weed-control-bermuda-grass', destination: '/blog/best-weed-control-bermuda-grass-safe-products-wont-kill', permanent: true },
  { source: '/blog/tenacity-mesotrione-application-guide', destination: '/blog/tenacity-herbicide-mesotrione-application-guide', permanent: true },
  { source: '/blog/what-kind-of-grass-id-guide-visual-identification-guide', destination: '/blog/what-kind-of-grass-do-i-have-visual-identification-guide', permanent: true },
  { source: '/blog/scotts-southern-lawn-builder-instructions', destination: '/blog/scotts-southern-turf-builder-instructions', permanent: true },
  { source: '/blog/how-to-apply-lime-to-lawn', destination: '/blog/when-to-apply-lime-to-lawn', permanent: true },
  // Doubled-slug scar from the same bulk find-replace fixed in the 2026-07-03
  // link audit (9 posts corrected in Directus). Googlebot crawled it first and
  // GSC flags it as a soft 404 — the streamed not-found shell serves 200.
  { source: '/blog/how-to-identify-your-grass-type-by-look-feel-by-look-feel', destination: '/blog/how-to-identify-your-grass-type-by-look-feel', permanent: true },

  // Zero-impression consolidation (2026-08-07): none of these 7 sources were
  // indexed (URL-inspection sweep; most last crawled during the Apr 29 - May 2
  // 5xx outage or rejected as duplicates). Each 301s into the live keeper that
  // owns the intent. Sources still need Directus archiving after deploy; the
  // PRUNE list from the same sweep is pending approval in the vault note.
  { source: '/blog/10-common-mistakes-to-avoid-when-using-weed-killers', destination: '/blog/best-lawn-weed-killer-beginners', permanent: true },
  { source: '/blog/7-key-benefits-of-overseeding-your-lawn', destination: '/blog/overseeding-best-practices', permanent: true },
  { source: '/blog/aeration-overseeding-and-topdressing-the-beginners-guide-to-thicker-grass', destination: '/blog/overseeding-best-practices', permanent: true },
  { source: '/blog/cool-season-vs-warm-season-grass-how-to-care-for-the-lawn-you-actually-have', destination: '/blog/cool-season-vs-warm-season-grass-guide', permanent: true },
  { source: '/blog/how-to-identify-poa-annua-annual-bluegrass', destination: '/blog/how-to-get-rid-of-poa-annua', permanent: true },
  { source: '/blog/how-to-install-artificial-grass-diy-vs-pro-installation', destination: '/blog/artificial-grass-installation-step-by-step-guide-2026', permanent: true },
  { source: '/blog/milorganite-application-guide-apply', destination: '/blog/milorganite-application-guide-best-uses-2026', permanent: true },
  { source: '/blog/perennial-ryegrass-guide', destination: '/guides/perennial-ryegrass', permanent: true },

  // Slug repair (2026-08-08): "lowwater" was a typo-slug on an unindexed post;
  // renamed with its drought-tolerant-lawn retarget so the URL matches the
  // target query. Directus slug updated in the same pass.
  { source: '/blog/lowwater-lawn-ideas', destination: '/blog/drought-tolerant-lawn-ideas', permanent: true },

  // Bermuda weed-and-feed consolidation (2026-08-24): two posts owned the same
  // head term with 100% query overlap. The 2026-guide keeper takes 192 clicks /
  // 5,929 impressions at pos 4.4 over 28 days; this source takes 26 / 2,462 at
  // pos 8.3 on the same queries. The source's unique damage-recovery block and
  // its annual-nitrogen FAQ were grafted into the keeper first.
  { source: '/blog/best-weed-feed-bermuda-grass', destination: '/blog/best-weed-feed-bermuda-grass-2026-guide', permanent: true },

  // Tank-mix consolidation (2026-09-21): the tool-announcement post outranked
  // the tool it announces. Live SERP for "how much 2,4-d per gallon of water
  // calculator": the AI Overview cites the post, the post sits at organic
  // slot 10, and /tools/tank-mix-calculator is absent from the top 25. The
  // post's own 90-day queries are the tool's queries (herbicide calculator,
  // surfactant per gallon), so the tool takes them outright.
  { source: '/blog/tank-mix-calculator-new-tool', destination: '/tools/tank-mix-calculator', permanent: true },
]

/**
 * Bare slugs (no `/blog/` prefix) that 301 elsewhere and therefore must never
 * appear in a sitemap. Derived from BLOG_REDIRECTS so the two stay in lockstep.
 * @type {Set<string>}
 */
export const REDIRECTED_BLOG_SLUGS = new Set(
  BLOG_REDIRECTS.map((r) => r.source.replace(/^\/blog\//, '')),
)

/**
 * Validate a redirect array for the failure modes Next.js does NOT catch on its
 * own: duplicate sources, self-loops, A->B->C chains, and A<->B 2-cycles.
 * (Next.js applies redirects in a single pass and does not follow chains, so a
 * stale chain silently strands users on a 301 hop.) Pure; returns a list of
 * human-readable problems, empty when the array is clean.
 *
 * Exercised by __tests__/data/blog-redirects.test.ts so a bad future entry fails
 * the deploy gate (npm run test:ci) before it can ship.
 *
 * @param {ReadonlyArray<{ source: string, destination: string, permanent?: boolean }>} redirects
 * @returns {string[]}
 */
export function validateRedirects(redirects) {
  const issues = []
  const sourceToDest = new Map()
  const seenSources = new Set()

  for (const { source, destination } of redirects) {
    if (source === destination) {
      issues.push(`self-loop: ${source} redirects to itself`)
    }
    if (seenSources.has(source)) {
      issues.push(`duplicate source: ${source}`)
    }
    seenSources.add(source)
    sourceToDest.set(source, destination)
  }

  // A destination that is itself a source means a multi-hop chain (A->B->C) or,
  // if it points back, a 2-cycle (A<->B).
  for (const { source, destination } of redirects) {
    if (sourceToDest.has(destination)) {
      const next = sourceToDest.get(destination)
      if (next === source) {
        issues.push(`2-cycle: ${source} <-> ${destination}`)
      } else {
        issues.push(
          `chain: ${source} -> ${destination} -> ${next} (Next.js does not follow chains; point ${source} straight at ${next})`,
        )
      }
    }
  }

  return issues
}
